import { FC, useCallback, useMemo, useState } from 'react'
import { Stack, ThemeProvider, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { CreativeUploadWizardStep } from './_main/CreativeUploadWizard.controller'
import { LessThanRequiredVisualsReason } from 'models/assignment'
import { MUIButton } from '../MUIButton'
import { MUIInputField } from '../MUIInputField'
import { MUIRadio } from '../MUIRadio'
import Modal from '../Modals/Modal/Modal'
import { ProductKind } from 'constants/product'
import { QueryStatus } from '../QueryStatus'
import { SelectableButtonsGrid } from '../SelectableButtonsGrid'
import { redesignMUITheme } from 'constants/styling/redesignTheme'
import { useCreativeUploadWizard } from './_main/CreativeUploadWizard.context'
import { useNavigate } from 'react-router'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useSubmitAssignment } from 'dataQueries'

/** Props for CT upload wizard submit modal */
interface Props {
  /** Whether the modal is open or not */
  isOpen: boolean
  /** onClose handler fnc */
  onClose: () => void
}

/**
 * Modal for submitting uploaded visuals
 */
export const CreativeUploadWizardSubmitModal: FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['ct_upload_wizard', 'enums', 'common'])
  const navigate = useNavigate()
  const { spawnSuccessToast } = useSnackbar()

  const submitMutation = useSubmitAssignment()

  const {
    requiredVisualAmount,
    uploadedRequiredVisualsAmount,
    areVisualsMissing,
    assignmentId,
    assignmentProduct,
    fileCtrl,
  } = useCreativeUploadWizard()

  const [insufficiencyReason, setInsufficiencyReason] = useState<LessThanRequiredVisualsReason>()
  const [insufficiencyComment, setInsufficiencyComment] = useState('')
  const [comment, setComment] = useState('')
  const [guidelinesCompliance, setGuidelinesCompliance] = useState<0 | 1>()

  const isFormValid = useMemo(() => {
    if (!areVisualsMissing) {
      if (guidelinesCompliance === undefined) return false
      if (guidelinesCompliance === 0 && !comment) return false
    } else {
      if (!insufficiencyReason || !insufficiencyComment) return false
    }

    return true
  }, [comment, guidelinesCompliance, insufficiencyComment, insufficiencyReason, areVisualsMissing])

  const reasons = useMemo(
    () => Object.values(LessThanRequiredVisualsReason).filter(
      (reason) => reason !== LessThanRequiredVisualsReason.DRONE_CANT_DO_BRACKETING || assignmentProduct?.kind === ProductKind.DRONE_PHOTO
    ),
    [assignmentProduct?.kind]
  )

  const handleSubmit = useCallback(() => {
    if (!isFormValid) return

    const requiredFilenames = fileCtrl.filesByTag[CreativeUploadWizardStep.REQUIRED_VISUALS]?.filter((file) => !!file.gcFilename).map((file) => file.gcFilename as string) ?? []
    if (!requiredFilenames.length) return

    const additionalFilenames = fileCtrl.filesByTag[CreativeUploadWizardStep.ADDITIONAL_VISUALS]?.filter((file) => !!file.gcFilename).map((file) => file.gcFilename as string) ?? []

    if (!assignmentProduct) {
      console.warn('Submit fnc rejected: missing assignmentProduct')
      return
    }

    submitMutation.mutate(
      {
        assignmentId,
        requiredVisualsFilenames: requiredFilenames,
        additionalVisualsFilenames: additionalFilenames,
        creativeCommentForBackbone: comment,
        product: assignmentProduct,
        minUploadCount: requiredVisualAmount,
        lessThanRequiredVisualsReason: areVisualsMissing
          ? {
            reason: insufficiencyReason!,
            comment: insufficiencyComment!,
          }
          : undefined
      },
      {
        onSuccess: () => {
          navigate(-1)
          spawnSuccessToast(t('modal.success_toast'))
        },
      }
    )
  }, [assignmentId, assignmentProduct, comment, fileCtrl.filesByTag, insufficiencyComment, insufficiencyReason, isFormValid, navigate, requiredVisualAmount, spawnSuccessToast, submitMutation, t, areVisualsMissing])

  return (
    <ThemeProvider theme={redesignMUITheme}>
      <Modal
        contentStyle={{ maxWidth: '60rem' }}
        isOpen={isOpen}
        title={t(areVisualsMissing ? 'modal.title_missing' : 'modal.title_normal')}
        onClose={() => onClose()}
        afterClosed={() => {
          setInsufficiencyComment('')
          setInsufficiencyReason(undefined)
          setComment('')
          setGuidelinesCompliance(undefined)
        }}
        footerContent={(
          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>

            {areVisualsMissing &&
              <Typography flex="1 1 auto" variant="bodySmall">
                {t('modal.disclaimer')}
              </Typography>
            }

            <MUIButton
              type="secondaryBorder"
              onClick={() => onClose()}
            >
              {t('common:Cancel')}
            </MUIButton>

            <MUIButton
              onClick={() => handleSubmit()}
              disabled={!isFormValid}
              isLoading={submitMutation.isPending}
            >
              {t('submit')}
            </MUIButton>

          </Stack>
        )}
      >
        <Stack gap={2}>

          {!areVisualsMissing &&
            <>

              <Stack gap={1}>

                <Typography variant="text-sm">
                  {t('modal.guidelines_label')}
                </Typography>

                <SelectableButtonsGrid<0 | 1> values={[
                  {
                    value: 0,
                    displayName: t('common:Yes'),
                  },
                  {
                    value: 1,
                    displayName: t('common:No')
                  },
                ]}
                  selectedValue={guidelinesCompliance}
                  onSelect={(value) => setGuidelinesCompliance(value)}
                  preventUnselect
                />

              </Stack>

              <MUIInputField
                label={t('modal.comment_label')}
                isMultiline
                value={comment}
                required={guidelinesCompliance === 0}
                onChange={(e) => setComment(e.target.value)}
              />

            </>
          }

          {areVisualsMissing &&
            <>

              <Typography variant="text-md">
                <Trans
                  t={t}
                  i18nKey="modal.missing_visuals_info"
                  values={{
                    uploaded: uploadedRequiredVisualsAmount,
                    required: requiredVisualAmount,
                  }}
                >
                  &nbsp;
                </Trans>
              </Typography>

              <Stack gap={1}>

                <Typography variant="text-md" fontSize="1.3rem">
                  {t('modal.reason_label')}
                </Typography>

                {reasons.map((reason) => (
                  <MUIRadio
                    key={reason}
                    label={t(`enums:less_than_required_visuals_reason.${reason}`)}
                    checked={insufficiencyReason === reason}
                    onChange={() => setInsufficiencyReason(reason)}
                  />
                ))}

              </Stack>

              <MUIInputField
                label={t('modal.reason_comment_label')}
                isMultiline
                required
                value={insufficiencyComment}
                onChange={(e) => setInsufficiencyComment(e.target.value)}
              />

            </>
          }

        </Stack>

        <QueryStatus
          query={submitMutation}
          showStates={['error']}
          spaceTopRem={2}
          spaceBottomRem={2}
        />

      </Modal>
    </ThemeProvider>
  )
}
